import { useState } from 'react';
import { csvName } from '@/lib/csv';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Mail, Pencil, Trash2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useCanEdit } from '@/lib/queries';
import { useEmailBatches, useEmailTemplates, type EmailBatch, type EmailTemplate } from '@/lib/email';
import { dateTime } from '@/lib/format';
import { adminLinks } from '@/lib/links';
import { ErrorNote, Spinner } from '@/components/Layout';
import { MERGE_FIELDS } from '@/lib/merge';
import { downloadCsv } from './AdminLayout';

/** The Email tab: write a new email, manage templates, and see what has been sent. */
export function AdminEmail() {
  const canEdit = useCanEdit();
  const batches = useEmailBatches();
  const templates = useEmailTemplates();
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-500">Emails go from EGKA Members. Replies go to the Membership Secretary, or to the instructor for emails sent from My dojo. Every email shows on the member's record.</p>
        {canEdit && <Link to="/admin/email/new" className="btn-primary"><Mail size={16} /> New email</Link>}
      </div>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Sent</h2>
        {batches.isLoading ? <Spinner /> : batches.error ? <ErrorNote>Could not load sent emails.</ErrorNote> : !batches.data?.length ? <p className="card text-sm text-ink-500">Nothing sent from here yet.</p> : (
          <div className="card overflow-x-auto p-0">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-stone-200 text-ink-500"><tr><th className="px-5 py-3 font-medium">Sent</th><th className="pr-3 font-medium">Subject</th><th className="pr-3 font-medium">To</th><th className="pr-3 font-medium">By</th><th className="pr-5 font-medium">Delivered</th></tr></thead>
              <tbody className="divide-y divide-stone-100">
                {batches.data.map(b => (
                  <tr key={b.id}>
                    <td className="whitespace-nowrap px-5 py-2.5">{dateTime(b.created_at)}</td>
                    <td className="pr-3"><Link to={adminLinks.emailBatch(b.id)} className="link">{b.subject}</Link></td>
                    <td className="pr-3">{b.audience_label}</td>
                    <td className="pr-3">{b.sent_by_name}{b.sent_as === 'instructor' && <span className="text-ink-500"> (instructor)</span>}</td>
                    <td className="pr-5">{b.sent_count} of {b.recipient_count}{b.failed_count > 0 && <span className="ml-2 pill bg-brand-100 text-brand-800">{b.failed_count} failed</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Templates</h2>
        <p className="text-sm text-ink-500">Fields filled in for each person: {MERGE_FIELDS.map(f => `{{${f.key}}}`).join(', ')}.</p>
        {templates.isLoading ? <Spinner /> : <Templates rows={templates.data ?? []} canEdit={canEdit} />}
      </section>
    </div>
  );
}

function Templates({ rows, canEdit }: { rows: EmailTemplate[]; canEdit: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [f, setF] = useState<TemplateFields>({ name: '', subject: '', body_text: '', for_instructors: false });
  const [error, setError] = useState('');
  const open = (t: EmailTemplate | null) => { setEditing(t?.id ?? 'new'); setF(t ? { name: t.name, subject: t.subject, body_text: t.body_text, for_instructors: t.for_instructors } : { name: '', subject: '', body_text: '', for_instructors: false }); setError(''); };
  async function save() {
    const { error } = editing === 'new' ? await supabase.from('email_template').insert(f) : await supabase.from('email_template').update(f).eq('id', editing!);
    if (error) { setError(/duplicate/.test(error.message) ? 'There is already a template with that name.' : `Could not save: ${error.message}`); return; }
    setEditing(null); await qc.invalidateQueries({ queryKey: ['email-templates'] });
  }
  async function remove(t: EmailTemplate) {
    if (!window.confirm(`Delete the template "${t.name}"? Emails already sent are kept.`)) return;
    const { error } = await supabase.from('email_template').delete().eq('id', t.id);
    if (error) setError(`Could not delete: ${error.message}`); else await qc.invalidateQueries({ queryKey: ['email-templates'] });
  }
  return (
    <div className="space-y-2">
      {rows.length === 0 && editing !== 'new' && <p className="card text-sm text-ink-500">No templates yet. Save one while writing an email, or add one here.</p>}
      {rows.map(t => editing === t.id ? <TemplateForm key={t.id} f={f} setF={setF} save={save} cancel={() => setEditing(null)} error={error} /> : (
        <div key={t.id} className="card flex flex-wrap items-start justify-between gap-3 py-3">
          <div className="min-w-0"><p className="font-semibold">{t.name}</p><p className="text-sm text-ink-500">{t.subject}{t.for_instructors && ' · instructors can use it'}</p></div>
          {canEdit && <div className="flex gap-1"><button className="btn-ghost px-2 py-1 text-sm" onClick={() => open(t)}><Pencil size={14} /> Edit</button><button className="btn-ghost px-2 py-1 text-sm" aria-label={`Delete ${t.name}`} onClick={() => remove(t)}><Trash2 size={14} /></button></div>}
        </div>
      ))}
      {editing === 'new' && <TemplateForm f={f} setF={setF} save={save} cancel={() => setEditing(null)} error={error} />}
      {canEdit && editing === null && <button className="btn-ghost border border-stone-300 text-sm" onClick={() => open(null)}>Add a template</button>}
    </div>
  );
}

type TemplateFields = { name: string; subject: string; body_text: string; for_instructors: boolean };
function TemplateForm({ f, setF, save, cancel, error }: { f: TemplateFields; setF: (f: TemplateFields) => void; save: () => void; cancel: () => void; error: string }) {
  return (
    <div className="card space-y-3">
      <label className="block"><span className="label">Template name</span><input className="input" value={f.name} maxLength={80} onChange={e => setF({ ...f, name: e.target.value })} /></label>
      <label className="block"><span className="label">Subject</span><input className="input" value={f.subject} maxLength={150} onChange={e => setF({ ...f, subject: e.target.value })} /></label>
      <label className="block"><span className="label">Message</span><textarea rows={8} className="input" value={f.body_text} onChange={e => setF({ ...f, body_text: e.target.value })} /></label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-brand-600" checked={f.for_instructors} onChange={e => setF({ ...f, for_instructors: e.target.checked })} /> Instructors can use this template</label>
      {error && <ErrorNote>{error}</ErrorNote>}
      <div className="flex gap-2"><button className="btn-primary" disabled={!f.name.trim() || !f.subject.trim() || !f.body_text.trim()} onClick={save}>Save template</button><button className="btn-ghost border border-stone-300" onClick={cancel}>Cancel</button></div>
    </div>
  );
}

/** One send: what was written, and every recipient with their status. */
export function AdminEmailBatch() {
  const { id = '' } = useParams();
  const batch = useQuery({
    queryKey: ['email-batch', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('email_batch').select('id,subject,body_text,audience,audience_label,recipient_count,sent_count,failed_count,sent_by_name,created_at,completed_at').eq('id', id).single();
      if (error) throw error;
      return data as EmailBatch;
    },
  });
  const rows = useQuery({
    queryKey: ['email-batch-rows', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('email_log').select('id,to_email,status,person:person_id(id,first_name,last_name)').eq('batch_id', id).order('to_email');
      if (error) throw error;
      return data as unknown as { id: string; to_email: string; status: string; person: { id: string; first_name: string; last_name: string } | null }[];
    },
  });
  if (batch.isLoading || rows.isLoading) return <Spinner />;
  if (batch.error || !batch.data) return <ErrorNote>Could not load this email.</ErrorNote>;
  const b = batch.data;
  const failed = (rows.data ?? []).filter(r => r.status === 'failed' && r.person);
  return (
    <div className="space-y-5">
      <Link to="/admin/email" className="text-sm text-ink-500 hover:underline">← Email</Link>
      <section className="card space-y-2">
        <h2 className="text-xl font-bold">{b.subject}</h2>
        <p className="text-sm text-ink-500">Sent {dateTime(b.created_at)} by {b.sent_by_name} to {b.audience_label} · {b.sent_count} of {b.recipient_count} delivered to SendGrid{b.failed_count > 0 && `, ${b.failed_count} failed`}</p>
        <pre className="whitespace-pre-wrap border-l-2 border-stone-200 pl-3 font-sans text-[15px] text-ink-700">{b.body_text}</pre>
      </section>
      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold">Recipients</h3>
          <span className="flex gap-2">
            {failed.length > 0 && <Link to="/admin/email/new" state={{ audience: { kind: 'people', person_ids: failed.map(r => r.person!.id) } }} className="btn-ghost border border-stone-300 text-sm"><Mail size={14} /> Email the {failed.length} that failed</Link>}
            <button className="btn-ghost border border-stone-300 text-sm" onClick={() => downloadCsv(csvName('Email recipients', [b.subject, `sent ${b.created_at.slice(0, 10)}`]), [['Name', 'Email', 'Status'], ...(rows.data ?? []).map(r => [r.person ? `${r.person.first_name} ${r.person.last_name}` : '', r.to_email, r.status])])}><Download size={14} /> Download</button>
          </span>
        </div>
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-stone-200 text-ink-500"><tr><th className="px-5 py-3 font-medium">Member</th><th className="pr-3 font-medium">Address</th><th className="pr-5 font-medium">Status</th></tr></thead>
            <tbody className="divide-y divide-stone-100">
              {(rows.data ?? []).map(r => (
                <tr key={r.id}>
                  <td className="px-5 py-2">{r.person ? <Link to={adminLinks.person(r.person.id)} className="link">{r.person.first_name} {r.person.last_name}</Link> : null}</td>
                  <td className="pr-3">{r.to_email}</td>
                  <td className="pr-5"><span className={`pill capitalize ${r.status === 'sent' ? 'bg-emerald-100 text-emerald-800' : r.status === 'failed' ? 'bg-brand-100 text-brand-800' : 'bg-sky-100 text-sky-800'}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
